import React, { useState, useRef, useMemo, useEffect } from "react";
import MainModal from "components/ModalMain";
import Button from "components/Button";
import Input from "components/Input";
import Dropdown from "components/Dropdown";
import Checkbox from "components/Checkbox";
import PhoneNumberInput from "components/PhoneNumberInput";
import ModalGeneralSuccessfully from "screens/ModalGeneralSuccessfully";
import ModalGeneralFailed from "screens/ModalGeneralFailed";
import { usePlacesWidget } from "react-google-autocomplete";
import { useQuery,useMutation } from "@apollo/client";
import { GET_ALL_STAFF_ROLES, CREATE_STAFF } from "graphql/service";
import colors from "constants/colors";
import { Container, Footer, Head, Row } from "./styles";
import { debouncedErrorMessage } from "utils/messageUtil";

const ModalAddStaff = ({
  buttonLabel,
  buttonStyle,
  setStaffs
}) => {

  const [isOpen, setIsOpen] = useState(false);
  const [successfullyIsOpen, setSuccessfullyIsOpen] = useState(false);
  const [failedIsOpen, setFailedIsOpen] = useState(false);

  const {data, error} = useQuery(GET_ALL_STAFF_ROLES)

  const options = useMemo(() => {
    if (!data?.validStaffRoles?.length) return [];

    return data.validStaffRoles.map((role) => ({
      label: role.name,
      value: role.roleId,
    }));
  }, [data]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  const [staffInfo, setStaffInfo] = useState({
    fullName: "",
    staffRoleIds: [],
    phoneNumber: "",
    email: "",
    streetAddress: "",
    streetAddressLine2: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
    isActive: true,
    PIN: "",
  });

  const [staffInfoError, setStaffInfoError] = useState({});

  const phoneNumber = staffInfo.phoneNumber || "";
  const [phoneNumberValidity, setPhoneNumberValidity] = useState(false);
  
  const updatingRef = useRef(false);

  const role=options.find((option) => option.value === staffInfo.staffRoleIds[0])?.label || ""

  const handlePhoneChange = (number) => {
    if (updatingRef.current) return;
    if (number === phoneNumber) return;
    // ignore library reset during init
    if (number === "" && phoneNumber !== "") return;
    updatingRef.current = true;
    setStaffInfo((prev) => ({ ...prev, phoneNumber: number }));
    updatingRef.current = false;
  };

  const handleValidityChange = (valid) => {
    setPhoneNumberValidity(valid);
  };

  const { ref } = usePlacesWidget({
    apiKey: `${process.env.REACT_APP_GOOGLE_APIKEY}&loading=async`,
    onPlaceSelected: (place) => {
      const components = place.address_components || [];

      const getComponent = (type) =>
        components.find((component) => component.types.includes(type))?.long_name || "";

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      const streetAddressLine2 = ""; // Google Autocomplete doesn't return this
      const city = getComponent("locality") || getComponent("sublocality") || "";
      const province = getComponent("administrative_area_level_1");
      const postalCode = getComponent("postal_code");
      const country = getComponent("country");
      setStaffInfo({
        ...staffInfo,
        streetAddress,
        streetAddressLine2,
        city,
        province,
        postalCode,
        country
      })
    },
    options: {
      types: ["address"],
    },
  });

  const [createStaff, { loading }] = useMutation(CREATE_STAFF, {
    onCompleted: (data) => {
      if (data.createStaff) {
        setStaffs((staffs) => [...staffs, data.createStaff]);
        setSuccessfullyIsOpen(true);
        setFailedIsOpen(false);
        setIsOpen(false);
      } else {
        setFailedIsOpen(true);
        setIsOpen(false);
      }
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message
        if(error.message.includes('Validation')) {
            content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception)
        }
        debouncedErrorMessage(content)
      }
    },
  });

  const handleContinue = () => {
    if(loading) return
    let hasError = false;

    if (staffInfo.fullName.trim() === "") {
      setStaffInfoError((prev) => ({ ...prev, fullNameError: true }));
      hasError = true;
    }

    if (staffInfo.staffRoleIds.length === 0) {
      setStaffInfoError((prev) => ({ ...prev, roleError: true }));
      hasError = true;
    }

    if (staffInfo.streetAddress.trim() === "") {
      setStaffInfoError((prev) => ({ ...prev, streetAddressError: true }));
      hasError = true;
    }

    if (staffInfo.email.trim() === "") {
      setStaffInfoError((prev) => ({ ...prev, emailError: true }));
      hasError = true;
    }

    if(staffInfo.phoneNumber.trim() === "" || !phoneNumberValidity) {
      setStaffInfoError((prev) => ({ ...prev, phoneNumberError: true }));
      hasError = true;
    }

    if (staffInfo.PIN.trim() === "" || staffInfo.PIN.length !== 6) {
      setStaffInfoError((prev) => ({ ...prev, PINError: true }));
      hasError = true;
    }

    if (hasError) return;

    createStaff({
      variables: {
        input: {
          fullName: staffInfo.fullName,
          staffRoleIds: staffInfo.staffRoleIds,
          email: staffInfo.email,
          phoneNumber: staffInfo.phoneNumber,
          address: staffInfo.streetAddress,
          addressLine2: staffInfo.streetAddressLine2,
          city: staffInfo.city,
          province: staffInfo.province,
          postalCode: staffInfo.postalCode,
          country: staffInfo.country,
          isActive: staffInfo.isActive,
          pin: staffInfo.PIN,
        },
      },
    });
  };

  return (
    <>
      <MainModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        buttonLabel={buttonLabel}
        buttonStyle={buttonStyle}
        className="modal-success"
        additionalstyle={`
        max-width: 500px;
        .modal-content{
          height: 100%;
          .close {
            z-index: 200;
          }   
          .modal-body{
            height: 100%;
            padding: 24px;
          }
        }
        background: ${colors.black5};
        top: 179px;
      `}
      >
        <Container>
          <Head>Staff Details</Head>
          <Row>
            <Input
              mainPage
              required
              title="NAME"
              value={staffInfo.fullName || ""}
              error={staffInfoError.fullNameError}
              onChange={(e) => {
                setStaffInfo({ ...staffInfo, fullName: e.target.value });
                setStaffInfoError({ ...staffInfoError, nameError: false });
              }}
              additionalstyle={`
              width: 50%;
            `}
            />
            <Dropdown
              mainPage
              required
              title="ROLE"
              options={options}
              error={staffInfoError.roleError}
              value={role}
              onChange={(e) => {
                setStaffInfo({ ...staffInfo, staffRoleIds: [e] });
                setStaffInfoError({ ...staffInfoError, roleError: false });
              }}
              additionalstyle={`
              width: 50%;
            `}
            />
          </Row>
          <Row>
            <Input
              mainPage
              required
              inputFieldRef={ref}
              title="ADDRESS"
              value={
                staffInfo.streetAddress +
                (staffInfo.streetAddressLine2 ? ", " + staffInfo.streetAddressLine2 : "") +
                (staffInfo.city ? ", " + staffInfo.city : "") +
                (staffInfo.province ? ", " + staffInfo.province : "") +
                (staffInfo.postalCode ? ", " + staffInfo.postalCode : "") +
                (staffInfo.country ? ", " + staffInfo.country : "")
              }
              error={staffInfoError.streetAddressError}
              onChange={(e) => {
                setStaffInfo({ ...staffInfo, streetAddress: e.target.value });
                setStaffInfoError({ ...staffInfoError, streetAddressError: false });
              }}
              additionalstyle={`
              width: 100%;
            `}
            />
          </Row>
          <Row>
            <Input
              mainPage
              required
              title="EMAIL"
              value={staffInfo.email || ""}
              error={staffInfoError.emailError}
              onChange={(e) => {
                setStaffInfo({ ...staffInfo, email: e.target.value });
                setStaffInfoError({ ...staffInfoError, emailError: false });
              }}
              additionalstyle={`
              width: 50%;
            `}
            />
            <PhoneNumberInput
              mainPage
              required
              title="PHONE"
              error={staffInfoError.phoneNumberError}
              value={phoneNumber}
              onChangeNumber={handlePhoneChange}
              onPhoneValidityChange={handleValidityChange}
              onBlur={() => {          
                setStaffInfoError((prev) => ({ 
                  ...prev,
                  phoneNumberError: !phoneNumberValidity && phoneNumber.length > 0
                }));
              }}
              additionalstyle={`
              width: 50%;
              @media only screen and (max-width: 767px) {
                width: 100%;
              }
            `}
            />
          </Row>
          <Row>
            <Input
              mainPage
              title="6-DIGIT PIN"
              maxLength={6}
              value={staffInfo.PIN}
              error={staffInfoError.PINError}
              errorMessage={"PIN must be 6 digits"}
              onChange={(e) => {
                const result = e.target.value.replace(/\D/g, "");
                setStaffInfo({ ...staffInfo, PIN: result });
                setStaffInfoError({ ...staffInfoError, PINError: false });
              }}
              additionalstyle={`
              width: calc(50% - 8px);
            `}
            />
          </Row>
          <Checkbox
            checked={!staffInfo.isActive}
            onChange={(e) => {
              setStaffInfo({ ...staffInfo, isActive: !staffInfo.isActive });
            }}
            title="Deactivate Account"
            additionalstyle={`
              margin: 24px 0 0 2px;
            `}
          />
        </Container>
        <Footer>
          <div className="buttonContainer">
            <Button
              danger
              additionalstyle={`
              width: 79px;
              margin-right: 16px;
            `}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              success
              additionalstyle={`
              width: 97px;
            `}
              onClick={handleContinue}
              data-loading={loading}
            >
              {loading? null: 'Continue'}
            </Button>
          </div>
        </Footer>
      </MainModal>
      <ModalGeneralSuccessfully
        isOpen={successfullyIsOpen}
        setIsOpen={setSuccessfullyIsOpen}
        role={role}
        fullName={staffInfo.fullName || ""}
        type="createStaff"
      />
      <ModalGeneralFailed
        isOpen={failedIsOpen}
        setIsOpen={setFailedIsOpen}
        retryFunction={handleContinue}
        type="createStaff"
      />
    </>
  );
};

export default ModalAddStaff;
